import type { ReactNode } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { EmergencyBar } from "./EmergencyBar";
import { useContactSettings } from "@/hooks/useContactSettings";
import { useHomeSettings } from "@/hooks/useHomeSettings";

export function PageShell({ children}: { children: ReactNode}) {
  const { settings } = useHomeSettings();
  const { settings: contactSettings } = useContactSettings();
  return (
    <main className="min-h-screen bg-background">
      <Header settings={settings?.header} phone={contactSettings?.phone}/>
      {children}
      <Footer />
      <EmergencyBar phone={contactSettings?.emergency_phone} />
    </main>
  );
}

export function PageHero({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  return (
    <section className="relative overflow-hidden bg-mesh-hero py-24 text-primary-foreground md:py-28">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-gold/15 blur-3xl animate-blob"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 bottom-0 h-[26rem] w-[26rem] rounded-full bg-secondary/30 blur-3xl animate-blob"
        style={{ animationDelay: "-8s" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-6">
        {eyebrow && (
          <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-gold backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-gold" />
            {eyebrow}
          </p>
        )}
        <h1 className="font-display text-4xl font-semibold leading-tight text-balance md:text-6xl">
          {title}
        </h1>
        {description && (
          <p className="mt-5 max-w-2xl text-lg text-primary-foreground/80">{description}</p>
        )}
      </div>
    </section>
  );
}

